import {
  type MenuWorksProduct,
  type ProductsSyncStatusResponseDTO,
  type PaginationConfig,
  SortOrder,
  type CDLRecordListMeta,
} from '@centric-os/types';
import { isEmpty, isNil } from 'lodash-es';
import { defineStore, type StateTree } from 'pinia';

interface ProductsListResponse {
  results?: MenuWorksProduct[];
  sync_status?: ProductsSyncStatusResponseDTO;
  meta?: CDLRecordListMeta;
}

const isAbortError = (err: unknown): boolean => {
  if (err instanceof Error) {
    if (err.name === 'AbortError' || (err as { code?: string }).code === 'ERR_CANCELED') {
      return true;
    }
    if (err.name === 'API Error' && (err as { message?: string }).message === 'canceled') {
      return true;
    }
  }
  return (
    typeof err === 'object' &&
    !isNil(err) &&
    (err as { name?: string }).name === 'API Error' &&
    (err as { message?: string }).message === 'canceled'
  );
};

const defaultMenuWorksProductsPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'mrn',
    sortOrder: SortOrder.ASC,
    search: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

interface State extends StateTree {
  menuWorksProducts: MenuWorksProduct[];
  isLoadingAll: boolean;
  loadedCount: number;
  totalResults: number;
  productsSyncStatus: ProductsSyncStatusResponseDTO | null;
  isLoadingSyncStatus: boolean;
  /** Unit ID of the currently loaded products (used to avoid clearing when refreshing same unit) */
  loadedUnitId: string | null;
}

/**
 * Creates and returns a Pinia store for managing MenuWorks products.
 * Uses pagination for server-side paging; compatible with StoreTable.
 *
 * @param {string} [storeId='menu-works-products'] - The unique identifier for the store.
 * @returns {ReturnType<typeof defineStore>} The Pinia store instance.
 */
export function useMenuWorksProductsStore(storeId = 'menu-works-products') {
  const store = defineStore(storeId, {
    state: (): State => ({
      menuWorksProducts: [],
      isLoadingAll: false,
      loadedCount: 0,
      totalResults: 0,
      productsSyncStatus: null,
      isLoadingSyncStatus: false,
      loadedUnitId: null,
    }),
    actions: {
      /**
       * Loads products for a MenuWorks business unit.
       * Reads limit, page, and search from store pagination when present.
       *
       * Pass `silent: true` during background polling to avoid resetting the product list
       * or showing loading indicators — the table stays populated while sync_status updates.
       *
       * @param {string} unitId - The business unit ID.
       * @param {object} [options] - Optional behavior flags.
       * @param {boolean} [options.silent] - When true, skips clearing products and loading indicators.
       * @returns {Promise<void>} A promise that resolves when data has been loaded.
       */
      async loadAllMenuWorksProducts(
        unitId: string,
        options?: { silent?: boolean },
      ): Promise<void> {
        if (isEmpty((unitId || '').trim())) return;

        this.abortControllers?.get('loadAllMenuWorksProducts')?.abort();
        this.abortControllers?.set('loadAllMenuWorksProducts', new AbortController());

        const qp = this.pagination?.queryParams;
        const limit = typeof qp?.limit === 'number' ? qp.limit : 25;
        const page = typeof qp?.page === 'number' ? qp.page : 1;
        const search = typeof qp?.search === 'string' ? qp.search : undefined;

        const hasProductsForUnit = this.loadedUnitId === unitId && !isEmpty(this.menuWorksProducts);
        const silent = options?.silent ?? hasProductsForUnit;

        if (!silent) {
          const isUnitChange = !isNil(this.loadedUnitId) && this.loadedUnitId !== unitId;
          if (isUnitChange) {
            this.menuWorksProducts = [];
            this.productsSyncStatus = null;
          }
          this.loadedUnitId = null;
          this.isLoadingAll = true;
          this.isLoadingSyncStatus = true;
          this.loadedCount = 0;
          this.totalResults = 0;
        }

        let loadError: unknown;
        try {
          const params: { limit: number; page: number; q?: string } = {
            limit,
            page,
          };
          if (search?.trim()) params.q = search.trim();

          const response = await this.cdlApi.get<ProductsListResponse>(
            `/menu/v3/menuworks/business_units/${unitId}/products`,
            {
              params,
              signal: this.abortControllers?.get('loadAllMenuWorksProducts')?.signal,
            },
          );

          const data = response.data;
          const syncStatus = data?.sync_status;
          this.menuWorksProducts = data?.results ?? [];
          this.loadedCount = data?.meta?.result_count ?? 0;
          this.totalResults = data?.meta?.total_results ?? this.loadedCount;
          if (syncStatus) {
            this.productsSyncStatus = { ...syncStatus };
          }
          this.loadedUnitId = unitId;
          this.updatePaginationWithMeta?.({
            totalResults: this.totalResults,
            resultCount: this.loadedCount,
            totalPages: Math.ceil(this.totalResults / limit) || 1,
          });
        } catch (err) {
          loadError = err;
          if (!isAbortError(err)) throw err;
        } finally {
          if (!isAbortError(loadError)) {
            this.isLoadingAll = false;
            this.isLoadingSyncStatus = false;
          }
        }
      },

      /**
       * Triggers a refresh of the MenuWorks products cache for a business unit.
       * POSTs to the sync endpoint; sync runs asynchronously on the server.
       * 204 = sync triggered; 409 = sync already in progress. In both cases we poll for status.
       *
       * @param {string} unitId - The business unit ID.
       * @returns {Promise<void>} A promise that resolves when the request completes.
       */
      async triggerProductsSync(unitId: string): Promise<void> {
        if (isEmpty((unitId || '').trim())) return;

        try {
          await this.cdlApi.post(`/menu/v3/menuworks/business_units/${unitId}/products/sync`);
        } catch (err) {
          const status = (err as { status?: number })?.status;
          if (status !== 409) throw err;
          // 409 = sync already in progress; treat like 204 and poll for status
        }
        await this.loadAllMenuWorksProducts(unitId);
      },
    },
    pagination: true,
    paginationConfig: defaultMenuWorksProductsPaginationConfig,
  });
  return store();
}
